/**
 * 기사 명세서 **공개 예약** 관리 (2026-09-16 사용자 요청).
 *
 * 🔴 **마감과 다른 축이다**(사용자 명시).
 *   · 마감(`result_finish`) = 담당자 쪽 **쓰기 잠금** — 금액이 더 이상 안 바뀐다.
 *   · 공개(`salary_publish`) = 기사 쪽 **읽기 개방** — 기사 눈에 보이기 시작한다.
 *   그래서 이 화면은 마감 화면(`SettleClose`)과 합치지 않는다. 대신 두 상태를 **나란히**
 *   보여주고 위험한 조합(공개했는데 아직 미마감)만 그 줄에서 경고한다.
 *
 * ⚠️ 기본값은 **공개**다 — 행이 없는 달은 열려 있다. 이 기능을 켜기 전부터 과거 111개월치가
 *    기사에게 보이고 있었기 때문에, "행이 있어야 공개"로 만들면 과거가 통째로 사라진다.
 *    그래서 **잠그는 것이 명시적 행위**이고 화면도 그렇게 읽히게 적었다.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox, dash } from '../components/DataTable';
import { openAtLabel } from '../driver/format';
import Modal from '../components/Modal';
import SalaryAppReach from './SalaryAppReach';

interface PublishRow {
  drive_mon: string;
  line_cnt: number;
  driver_cnt: number;
  open_at: string | null;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
  published: boolean;
  scheduled: boolean;
  is_finish: boolean;
}

/** 지금 `datetime-local` 에 넣기 좋은 기본값 — 오늘 18:00(정산 마무리 시각대). */
function defaultOpenAt(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T18:00`;
}

export default function SalaryPublishPanel({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<PublishRow[] | null>(null);
  const [now, setNow] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** 어느 달의 예약 입력칸이 열려 있나 + 그 값. 줄마다 따로 둔다. */
  const [editing, setEditing] = useState<{ mon: string; at: string } | null>(null);
  /** 「기사앱」 — 그 달 명세 기사가 앱으로 볼 수 있는지(10-20 팀장회의 #39). */
  const [reachMon, setReachMon] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    // 서버가 주는 행은 `Record<string, unknown>` 이다. 이 화면이 쓰는 칸 이름은 SQL 과 1:1 이라
    // 여기서 한 번만 좁힌다 — 칸 이름이 바뀌면 이 캐스트가 아니라 화면이 빈칸으로 티를 낸다.
    (api.listSalaryPublish({ limit: 24 }) as unknown as Promise<{
      rows: PublishRow[];
      now: string | null;
    }>)
      .then((r) => {
        setRows(r.rows);
        setNow(r.now);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : '공개 상태를 불러오지 못했습니다.');
        setRows([]);
      });
  }, []);

  useEffect(load, [load]);

  async function apply(driveMon: string, mode: 'lock' | 'open' | 'schedule', openAt?: string) {
    setBusy(driveMon);
    setError(null);
    try {
      await api.setSalaryPublish({ driveMon, mode, openAt });
      setEditing(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '바꾸지 못했습니다.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h2 className="text-lg font-bold">기사 명세서 공개</h2>
          <p className="mt-1 text-sm text-ink-soft">
            잠그면 기사앱에서 그 달이 <b>「준비 중」</b>으로만 보이고 금액은 내려가지 않습니다.
            <br />
            <span className="text-ink-faint">
              마감(쓰기 잠금)과는 별개입니다 — 마감해도 공개하지 않으면 기사는 못 봅니다.
            </span>
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {/* 🔴 서버 시계를 보여준다 — 예약 판정은 DB 가 한다. 브라우저 시계가 틀려도 헷갈리지 않게. */}
          {now && <span className="text-xs text-ink-faint">서버 기준 {now.replace('T', ' ')}</span>}
          <button className="btn-ghost" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="card overflow-auto" style={{ maxHeight: 'max(24rem, calc(var(--app-h) - 20rem))' }}>
        <table className="w-full">
          <thead className="sticky top-0 z-20">
            <tr>
              {['정산년월', '명세', '기사 화면', '마감', ''].map((h, i) => (
                <th key={h || i} className="th bg-surface">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td className="td text-ink-faint" colSpan={5}>
                  불러오는 중…
                </td>
              </tr>
            )}
            {rows?.length === 0 && (
              <tr>
                <td className="td text-ink-faint" colSpan={5}>
                  명세가 있는 달이 없습니다.
                </td>
              </tr>
            )}
            {rows?.map((r) => (
              <tr key={r.drive_mon} className="hover:bg-canvas">
                <td className="td font-semibold">{r.drive_mon}</td>
                <td className="td text-ink-soft">
                  {r.driver_cnt.toLocaleString('ko-KR')}명 · {r.line_cnt.toLocaleString('ko-KR')}줄
                  <button
                    className="btn-ghost ml-2 h-7 px-2 py-0 text-xs"
                    title="이 달 명세 기사가 기사앱으로 볼 수 있는지(로그인·번호 겹침·알림)"
                    onClick={() => setReachMon(r.drive_mon)}
                  >
                    기사앱
                  </button>
                </td>
                <td className="td">
                  {r.published ? (
                    <span className="font-semibold text-emerald-700">공개됨</span>
                  ) : r.scheduled ? (
                    <span className="font-semibold text-amber-700">
                      예약 · {openAtLabel(r.open_at)} 공개
                    </span>
                  ) : (
                    <span className="font-semibold text-ink-soft">잠김 (준비 중)</span>
                  )}
                  {r.note && <span className="ml-2 text-xs text-ink-faint">{dash(r.note)}</span>}
                  {/* 🔴 위험한 조합만 그 줄에서 경고한다 — 공개했는데 금액이 아직 바뀔 수 있는 상태. */}
                  {r.published && !r.is_finish && (
                    <div className="text-xs text-amber-700">
                      아직 미마감 — 기사가 보는 금액이 바뀔 수 있습니다.
                    </div>
                  )}
                </td>
                <td className="td text-ink-soft">{r.is_finish ? '마감' : '—'}</td>
                <td className="td">
                  {editing?.mon === r.drive_mon ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <input
                        type="datetime-local"
                        className="field h-8 w-auto py-0 text-sm"
                        value={editing.at}
                        onChange={(e) => setEditing({ mon: r.drive_mon, at: e.target.value })}
                      />
                      <button
                        className="btn-primary h-8 px-3 py-0 text-sm"
                        disabled={busy === r.drive_mon || !editing.at}
                        onClick={() => apply(r.drive_mon, 'schedule', editing.at)}
                      >
                        예약
                      </button>
                      <button className="btn-ghost h-8 px-3 py-0 text-sm" onClick={() => setEditing(null)}>
                        취소
                      </button>
                    </span>
                  ) : (
                    <span className="flex flex-wrap gap-2">
                      <button
                        className="btn-ghost h-8 px-3 py-0 text-sm"
                        disabled={busy === r.drive_mon}
                        onClick={() => setEditing({ mon: r.drive_mon, at: defaultOpenAt() })}
                      >
                        예약…
                      </button>
                      {r.published ? (
                        <button
                          className="btn-ghost h-8 px-3 py-0 text-sm"
                          disabled={busy === r.drive_mon}
                          onClick={() => apply(r.drive_mon, 'lock')}
                        >
                          잠그기
                        </button>
                      ) : (
                        <button
                          className="btn-primary h-8 px-3 py-0 text-sm"
                          disabled={busy === r.drive_mon}
                          onClick={() => apply(r.drive_mon, 'open')}
                        >
                          지금 공개
                        </button>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-ink-faint">
        한 번도 손대지 않은 달은 <b>공개 상태</b>입니다 — 이 기능을 켜기 전부터 기사에게 보이던
        과거 명세를 그대로 두기 위해서입니다. 감추려면 그 달을 <b>잠그기</b> 하세요.
      </p>

      {reachMon && (
        <Modal width="max-w-5xl">
          <SalaryAppReach driveMon={reachMon} onClose={() => setReachMon(null)} />
        </Modal>
      )}
    </div>
  );
}
